"use client"

/**
 * MoneyPageLink — Hover preview for money page links (hub pages, collections)
 * 
 * Money pages = pages with affiliate links that generate revenue.
 * Tools are NOT money pages — use plain <a> tags for tools.
 * 
 * Preview shows: real product image + title + a browse/shop label
 * Desktop: hover shows preview card above the link
 * Mobile: @media(hover:none) hides preview entirely — zero DOM weight on touch
 */

import { useState } from 'react'
import Link from 'next/link'

interface MoneyPageLinkProps {
  href: string
  type: 'hub' | 'collection'
  title: string
  description?: string
  image?: string   // Real product image URL from Supabase products table
  children: React.ReactNode
  className?: string
}

export function MoneyPageLink({ href, type, title, description, image, children, className }: MoneyPageLinkProps) {
  const [isHovered, setIsHovered] = useState(false)

  const previewLabel = type === 'hub' ? 'Browse the guide' : 'Shop the collection'

  return (
    <span
      className={`money-page-link-wrapper relative inline ${className || ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        href={href}
        className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-600 transition-colors font-medium"
      >
        {children}
      </Link>

      {/* Preview card — positioned ABOVE link, hidden on touch devices */}
      <span
        className={`money-page-preview absolute z-[100] left-0 bg-white border border-gray-200 rounded-lg shadow-xl transition-all duration-200 pointer-events-none ${
          isHovered ? 'opacity-100' : 'opacity-0'
        } ${image ? 'w-72 p-0 overflow-hidden' : 'w-64 p-3'}`}
        style={{ bottom: '100%', marginBottom: '8px' }}
        role="tooltip"
      >
        {/* Arrow pointing down */}
        <span className="absolute top-full left-4 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-white" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }} />

        {/* Product image */}
        {image && (
          <span className="block w-full h-28 bg-gray-50 overflow-hidden">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-contain p-2"
              loading="lazy"
            />
          </span>
        )}

        {/* Text content */}
        <span className={`block ${image ? 'px-3 py-2' : ''}`}>
          <span className="block text-sm font-semibold text-gray-900 mb-0.5 line-clamp-2">{title}</span>
          {description && !image && (
            <span className="block text-xs text-gray-600 mb-1.5 line-clamp-2">{description}</span>
          )}
          <span className="block text-xs text-blue-600 font-medium">{previewLabel} →</span>
        </span>
      </span>

      {/* CSS: hide preview on touch devices */}
      <style jsx>{`
        @media (hover: none) {
          .money-page-preview {
            display: none !important;
          }
        }
      `}</style>
    </span>
  )
}
